import React from 'react'
import { useMsal, MsalProvider } from '@azure/msal-react'
import { PublicClientApplication, EventType } from '@azure/msal-browser'
import { Button } from '@/components/Common/Button'
import { msalConfig, loginRequest } from '@/config/msalConfig'

// Microsoft icon component
const MicrosoftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 23 23" fill="none" aria-hidden="true" focusable="false">
    <path fill="#f25022" d="M1 1h9v9H1z" />
    <path fill="#00a4ef" d="M1 12h9v9H1z" />
    <path fill="#7fba00" d="M12 1h9v9h-9z" />
    <path fill="#ffb900" d="M12 12h9v9h-9z" />
  </svg>
)

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig)

// Default to using Popup APIs for login
msalInstance.initialize().then(() => {
  // Register event listeners for auth state changes
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS) {
      console.log('Microsoft login successful')
    }
  })
})

interface MicrosoftLoginButtonProps {
  onSuccess: (accessToken: string) => void
  onError: () => void
  disabled?: boolean
  className?: string
}

const MicrosoftLoginButtonBase: React.FC<MicrosoftLoginButtonProps> = ({ onSuccess, onError, disabled = false, className }) => {
  const { instance } = useMsal()
  const [isLoading, setIsLoading] = React.useState(false)

  const handleLogin = async () => {
    if (disabled || isLoading) return

    setIsLoading(true)
    try {
      const response = await instance.loginPopup(loginRequest)
      if (response.account) {
        // Get access token silently using the account we got from the login response
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account: response.account,
        })

        onSuccess(tokenResponse.accessToken)
      } else {
        onError()
      }
    } catch (error) {
      console.error('Microsoft login error:', error)
      onError()
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Button
      onClick={handleLogin}
      className={className ?? 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm font-medium'}
      leftIcon={<MicrosoftIcon />}
      disabled={disabled || isLoading}
    >
      {isLoading ? 'Signing in...' : 'Sign in with Microsoft'}
    </Button>
  )
}

// Wrapped component with MsalProvider
export const MicrosoftLoginButton: React.FC<MicrosoftLoginButtonProps> = (props) => {
  return (
    <MsalProvider instance={msalInstance}>
      <MicrosoftLoginButtonBase {...props} />
    </MsalProvider>
  )
}

export default MicrosoftLoginButton
